import { Fragment, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import styles from "./style.module.scss";
import cx from "classnames";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { TenantApi, TenantDataType } from "@/api/TenantApi";
import debounce from "debounce";
import { showAlert } from "@utils/Alert";
import LoadingScreen from "@components/content/LoadingScreen";
import { SuscriptionTypeApi, SuscriptionTypeDataType } from "@/api/SuscriptionTypeApi";
import { CurrencyApi, CurrencyDataType } from "@/api/CurrencyApi";
import { Alert, Button, Col, OffcanvasBody, OffcanvasHeader, Row, Table } from "reactstrap";
import DefaultOffCanvas from "@components/layout/Offcanvas/DefaultOffCanvas";
import { formatMoneyValue } from "@utils/Currency";
import { TENANT_STATUSES, ONVO_TENANT_PAYMENT_TYPE_CODE } from "@utils/Constants";
import moment from "moment";
import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { faCheck, faExclamation, faStop, faUsers, faFileInvoice } from "@fortawesome/free-solid-svg-icons";
import ConfirmableButton from "@components/content/ConfirmableButton";
import { deactiveOnvoSuscription, startPlanUpgrade } from "@/api/OnvoPayApi";
import { TenantPaymentApi, TenantPaymentDataType } from "@/api/TenantPaymentApi";
import { toNumberFormat } from "@utils/Utils";
import { setPlanFeatures, setPlanDocumentLimits, setTenantData } from "@/store/slices/suscriptionSlides";
import { PLAN_FEATURE_CATALOG, isFeatureAllowed, parsePlanFeatures } from "@/security/features.catalog";
import { DOCUMENT_LIMIT_CATALOG, formatLimitLabel, getDocumentLimit, parseDocumentLimits } from "@/security/documentLimits.catalog";
import { DocumentUsage, loadMonthlyDocumentUsage } from "@utils/documentLimitUsage";

type TenantManageProps = {
}

const TenantManage = (props: TenantManageProps) => {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const blockedFeature = searchParams.get("feature")
  const containerRef = useRef<HTMLDivElement>(null);
  const suscription = useAppSelector(state => state.suscription.suscription)
  const [onvoLoaded, setOnvoLoaded] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [paymentMethodAdded, setPaymentMethodAdded] = useState<boolean>(false)
  const [tenant, setTenant] = useState<TenantDataType>({})
  const [tenantPayments, setTenantPayments] = useState<TenantPaymentDataType[]>([])
  const [suscriptionType, setSuscriptionType] = useState<SuscriptionTypeDataType>({})
  const [currency, setCurrency] = useState<CurrencyDataType>({})
  const [currencies, setCurrencies] = useState<CurrencyDataType[]>([])
  const [availablePlans, setAvailablePlans] = useState<SuscriptionTypeDataType[]>([])
  const [selectedPlan, setSelectedPlan] = useState<SuscriptionTypeDataType | null>(null)
  const [upgradeOnvoData, setUpgradeOnvoData] = useState<{ onvoCustomerId: string; onvoSubscriptionId: string } | null>(null)
  const [compareOpen, setCompareOpen] = useState<boolean>(false)
  const [documentUsage, setDocumentUsage] = useState<DocumentUsage[]>([])
  const onvoFields = useRef<HTMLDivElement>(null)
  const onvoUpgradeFields = useRef<HTMLDivElement>(null)
  const paymentSectionRef = useRef<HTMLDivElement>(null)
  const needPaymentAssignment = tenant?.status == "pendingPaymentMethod" || tenant?.status == "paused";
  const toCancelSuscription = tenant?.status == "toCancel";
  const isFreePlan = !suscriptionType.onvoPriceId;
  const showPaymentForm = needPaymentAssignment && !paymentMethodAdded;

  // Los planes se muestran ordenados por monto ascendente (definición incremental:
  // cada plan incluye todo lo del anterior + funciones nuevas). Incluye el plan actual.
  const sortedPlans = useMemo(() => {
    return [...availablePlans].sort((a, b) => Number(a.monto || 0) - Number(b.monto || 0))
  }, [availablePlans])

  // Plan recomendado: el de pago más barato (distinto del actual) que habilita la función bloqueada.
  const recommendedPlanId = useMemo(() => {
    if (!blockedFeature) return null
    const eligible = sortedPlans
      .filter(p => p.id !== suscriptionType.id && Number(p.monto || 0) > 0 && isFeatureAllowed(parsePlanFeatures(p.features), blockedFeature))
    return eligible[0]?.id ?? null
  }, [sortedPlans, blockedFeature, suscriptionType.id])

  useEffect(() => {
    if (suscription?.id) {
      loadTenantData(suscription.id)
    }
  }, [suscription]);

  useEffect(() => {
    if (tenant?.id && needPaymentAssignment && !onvoLoaded && !upgradeOnvoData) {
      loadOnvoPay(tenant, onvoFields.current)
    }
  }, [tenant, needPaymentAssignment, onvoLoaded, upgradeOnvoData]);

  useEffect(() => {
    if (tenant?.id && selectedPlan && upgradeOnvoData && !onvoLoaded && onvoUpgradeFields.current) {
      loadOnvoPay({ ...tenant, ...upgradeOnvoData }, onvoUpgradeFields.current)
    }
  }, [tenant, selectedPlan, upgradeOnvoData, onvoLoaded]);

  // Hace scroll al formulario de pago SOLO cuando el widget de ONVO ya se cargó
  // (el contenedor gana altura real). Antes de eso el contenedor está vacío y el
  // scroll no cae en el lugar correcto.
  useEffect(() => {
    if (!onvoLoaded || !selectedPlan) return;
    const target = onvoUpgradeFields.current;
    const section = paymentSectionRef.current;
    if (!target || !section) return;

    let done = false;
    let fallback: ReturnType<typeof setTimeout> | undefined;
    const scrollToForm = () => {
      if (done || target.offsetHeight <= 80) return;
      done = true;
      section.scrollIntoView({ behavior: "smooth", block: "start" });
      observer.disconnect();
      if (fallback) clearTimeout(fallback);
    };
    const observer = new ResizeObserver(scrollToForm);
    observer.observe(target);
    // Fallback: si el observer no dispara, hace scroll de todas formas.
    fallback = setTimeout(() => {
      if (!done) section.scrollIntoView({ behavior: "smooth", block: "start" });
      observer.disconnect();
    }, 2000);
    scrollToForm(); // por si el formulario ya tiene altura

    return () => {
      observer.disconnect();
      if (fallback) clearTimeout(fallback);
    };
  }, [onvoLoaded, selectedPlan]);

  const loadTenantData = debounce(async (suscriptionId: string, syncStore = false) => {
    setLoading(true)
    try {
      const tenantData = await TenantApi.fetchTenantBySuscriptionId(suscriptionId)
      const suscriptionType = await SuscriptionTypeApi.get(`${tenantData.suscriptionTypeId}`)
      setTenant(tenantData);
      if (syncStore) {
        dispatch(setTenantData(tenantData))
      }
      setSuscriptionType(suscriptionType);
      loadDocumentUsage(suscriptionType);

      // La moneda del plan actual es opcional (el plan gratis puede no tenerla).
      try {
        if (suscriptionType.currencyId) {
          const currencyData = await CurrencyApi.get(`${suscriptionType.currencyId}`)
          setCurrency(currencyData);
        }
      } catch (err) {
        console.error("No fue posible obtener la moneda del plan actual", err)
      }

      const { items: tenantPayments } = await TenantPaymentApi.fetchAll()
      setTenantPayments(tenantPayments);
      await loadUpgradeOptions(suscriptionType)
    } finally {
      setLoading(false)
    }
  }, 200)

  const loadUpgradeOptions = async (currentPlan: SuscriptionTypeDataType) => {
    const { items: plans } = await SuscriptionTypeApi.fetchAllByListNoTenant({}, undefined, 100)
    const { items: currencyList } = await CurrencyApi.fetchAll()
    const paidPlans = plans.filter(p =>
      Number(p.monto || 0) > 0 && !!p.onvoPriceId && p.id !== currentPlan.id
    )
    // Incluye el plan actual junto a los de pago para mostrarlo en la lista.
    const display = currentPlan?.id ? [currentPlan, ...paidPlans] : paidPlans
    setCurrencies(currencyList)
    setAvailablePlans(display)
  }

  // Carga el uso de documentos del mes en curso para el plan actual (aceptados por tipo).
  const loadDocumentUsage = async (currentPlan: SuscriptionTypeDataType) => {
    const limits = parseDocumentLimits(currentPlan.documentLimits)
    if (!limits) {
      setDocumentUsage([])
      return
    }
    try {
      setDocumentUsage(await loadMonthlyDocumentUsage(limits))
    } catch (err) {
      console.error("No fue posible cargar el uso de documentos del mes", err)
      setDocumentUsage([])
    }
  }

  // Etiquetas de los topes mensuales del plan ("5 facturas / mes"), para destacarlos.
  const getPlanLimitLabels = (plan: SuscriptionTypeDataType): string[] => {
    const limits = parseDocumentLimits(plan.documentLimits)
    if (!limits) return []
    return DOCUMENT_LIMIT_CATALOG
      .map(node => ({ node, limit: getDocumentLimit(limits, node.id) }))
      .filter(({ limit }) => limit > 0)
      .map(({ node, limit }) => formatLimitLabel(node.id, limit))
  }

  const getCurrencyNumericId = (currencyId?: string | null): number => {
    const found = currencies.find(c => c.id === currencyId)
    return Number(found?.currency_id || 1)
  }

  // Ordena los nodos poniendo primero las funciones marcadas como principales en el plan.
  const orderByPrincipal = (nodes: typeof PLAN_FEATURE_CATALOG, plan: SuscriptionTypeDataType): typeof PLAN_FEATURE_CATALOG => {
    const principal = parsePlanFeatures(plan.principalFeatures)
    return [...nodes].sort((a, b) => {
      const aP = principal?.[a.id] === true ? 0 : 1
      const bP = principal?.[b.id] === true ? 0 : 1
      return aP - bP
    })
  }

  // Una función es visible en un plan si está permitida y NO está marcada como oculta.
  // Las funciones ocultas siguen aplicando en los permisos, solo no se muestran en la UI de planes.
  const isFeatureVisible = (plan: SuscriptionTypeDataType, featureId: string): boolean => {
    const allowed = isFeatureAllowed(parsePlanFeatures(plan.features), featureId)
    const hidden = parsePlanFeatures(plan.hiddenFeatures)?.[featureId] === true
    return allowed && !hidden
  }

  // Todas las funciones visibles que incluye el plan (para el detalle), principales primero.
  const getPlanFeatureLabels = (plan: SuscriptionTypeDataType): string[] => {
    const visible = PLAN_FEATURE_CATALOG.filter(node => isFeatureVisible(plan, node.id))
    return orderByPrincipal(visible, plan).map(node => node.label)
  }

  // Funciones visibles que el plan agrega respecto al plan inmediatamente anterior, principales primero.
  const getDeltaFeatureLabels = (plan: SuscriptionTypeDataType, prevPlan?: SuscriptionTypeDataType): string[] => {
    const prevFeatures = prevPlan ? parsePlanFeatures(prevPlan.features) : ({} as Record<string, boolean>)
    const delta = PLAN_FEATURE_CATALOG.filter(node => isFeatureVisible(plan, node.id) && !isFeatureAllowed(prevFeatures, node.id))
    return orderByPrincipal(delta, plan).map(node => node.label)
  }

  const getPlanUsersLabel = (plan: SuscriptionTypeDataType): string => {
    const max = Number(plan.maxUsers || 0)
    if (!max) return "Usuarios ilimitados"
    return max === 1 ? "1 usuario" : `Hasta ${max} usuarios`
  }

  // Marca un plan como el que el cliente quiere suscribirse y muestra el formulario ONVO
  // debajo de las tarjetas. Cierra el comparador si estaba abierto.
  const subscribeToPlan = async (plan: SuscriptionTypeDataType) => {
    setCompareOpen(false)
    setSelectedPlan(plan)
    setPaymentMethodAdded(false)
    setOnvoLoaded(false)
    setUpgradeOnvoData(null)
    setLoading(true)
    try {
      const session = await startPlanUpgrade(tenant, plan)
      setUpgradeOnvoData(session)
    } catch (err) {
      console.error("Error iniciando el upgrade de plan", err)
      showAlert("danger", "No fue posible iniciar el proceso de pago. Intente nuevamente o contacte a soporte.")
      setSelectedPlan(null)
    } finally {
      setLoading(false)
    }
  }

  const cancelSubscription = () => {
    setSelectedPlan(null)
    setUpgradeOnvoData(null)
    setOnvoLoaded(false)
  }

  const syncSuscripcionData = async (tenantData: TenantDataType, data: any) => {
    try {
      setLoading(true);
      setPaymentMethodAdded(true);
      const updatePayload: Partial<TenantDataType> = selectedPlan
        ? {
          onvoPaymentMethodId: data.paymentMethodId,
          onvoCustomerId: tenantData.onvoCustomerId,
          onvoSubscriptionId: tenantData.onvoSubscriptionId,
          suscriptionTypeId: selectedPlan.id,
          tipoPago: ONVO_TENANT_PAYMENT_TYPE_CODE,
          status: "active",
        }
        : {
          onvoPaymentMethodId: data.paymentMethodId,
          status: "active",
        };
      await TenantApi.update(`${tenantData.id}`, updatePayload)
      // Actualiza el gating por plan sin requerir recargar la app.
      if (selectedPlan) {
        dispatch(setPlanFeatures(parsePlanFeatures(selectedPlan.features)))
        dispatch(setPlanDocumentLimits(parseDocumentLimits(selectedPlan.documentLimits)))
      }
      showAlert("success", "El metodo de pago ha sido agregado exitosamente");
      setSelectedPlan(null);
      setUpgradeOnvoData(null);
      setCompareOpen(false);
      loadTenantData(`${suscription?.id}`, true);
    } catch (err) {

    } finally {
      setLoading(false);
    }
  }

  const loadOnvoPay = (tenantData: TenantDataType, targetEl: Element | null) => {
    if (window.onvo && targetEl) {
      // Limpia cualquier formulario previo para no renderizar más de uno.
      targetEl.innerHTML = "";
      window.onvo.pay({
        onError: (data: any) => {
          showAlert("danger", "No fue posible")
          console.log('error', data);
        },
        onSuccess: (data: any) => {
          syncSuscripcionData(tenantData, data)
          console.log('success', data);
        },
        publicKey: 'onvo_test_publishable_key_5NAA2U_A9-ZkYVt8nFg0jwnMib5N11vEbAE3HMkDL72kcf7N7U1JAbsimt94xxdmhIO6lyuSh9i4lpW9nBBNEw',
        subscriptionId: `${tenantData.onvoSubscriptionId}`,
        paymentType: "subscription",
        customerId: `${tenantData.onvoCustomerId}`
      }).render(targetEl);
      setOnvoLoaded(true)
    }
  }

  const getSuscriptionStatusIconProps = (): FontAwesomeIconProps => {
    let iconSvg = faExclamation;
    if (tenant.status == "active" || tenant.status == "trialing") {
      iconSvg = faCheck
    } else {
      if (tenant.status == "paused" || tenant.status == "canceled") {
        iconSvg = faStop
      }
    }
    return {
      icon: iconSvg,
      className: styles.statusIcon
    }

  }
  const getSuscriptionStatus = (): string => {
    if (tenant?.status) {
      return TENANT_STATUSES[tenant.status]
    }
    return ""
  }

  const getValidAccessUntilDate = () => {
    if (tenant?.accessUntil) {
      const date = moment(tenant.accessUntil, "YYYY-MM-DD")
      return date.format("DD-MM-YYYY")
    }
    return ""
  }

  const stopSuscription = async () => {
    try {
      setLoading(true)
      const isDeactivate = await deactiveOnvoSuscription(tenant)
      if (isDeactivate) {
        loadTenantData(`${suscription?.id}`, true)
        showAlert("success", "La suscripcion ha sido desactivada correctamente")
      } else {
        showAlert("danger", "No fue possible desactivar su suscripcion, por favor contacte a servicio al cliente")
      }
    } catch (err) {
      showAlert("danger", "No fue possible desactivar su suscripcion, por favor contacte a servicio al cliente")
    } finally {
      setLoading(false)
    }
  }

  const getStatusLabel = (status: string): ReactNode => {
    let statusLabel = ""
    switch (status) {
      case "succeeded":
        statusLabel = "Exitoso";
        break;
      case "failed":
        statusLabel = "Fallido";
        break;
      case "refunded":
        statusLabel = "Rembolsado";
        break;
      case "pending":
        statusLabel = "Pendiente";
        break;
      default:
        statusLabel = ""
    }

    return (<span className={cx([styles[`status-${status}`], styles.paymentStatusWrapper])}>
      {statusLabel}
    </span>)

  }

  const historialDePagos = (): ReactNode => {
    const rows = tenantPayments.map(tp => {
      const date = moment(tp.createdAt, "YYYY-MM-DD")
      const formattedDate = date.format("DD-MM-YYYY")
      const monto = toNumberFormat((Number(tp?.amount || 0) / 100))

      return (
        <tr>
          <td>
            {formattedDate}
          </td>
          <td>
            {tp.description || ""}
          </td>
          <td className="text-center">
            {monto}
          </td>
          <td className="text-center">
            {tp.currency}
          </td>
          <td className="text-center">
            {getStatusLabel(`${tp.status || ""}`)}
          </td>
          <td>
            {tp.refNumber}
          </td>
        </tr>
      )
    })
    return (
      <>
        <hr />
        <h3>Historial de Pagos</h3>
        <Row>
          <Col md={{
            size: 10,
            offset: 1
          }}>

            <Table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Descripcion</th>
                  <th className="text-center">Monto</th>
                  <th className="text-center">Moneda</th>
                  <th className="text-center">Estado Cobro</th>
                  <th># Referencia</th>
                </tr>
              </thead>
              <tbody>
                {rows}
              </tbody>
            </Table>
          </Col>
        </Row>
      </>
    )

  }
  // Uso de documentos del mes en curso para el plan actual (disponibles = límite - aceptados).
  const renderDocumentUsage = (): ReactNode => {
    if (documentUsage.length === 0) return null
    return (
      <>
        <hr />
        <h5><b>Documentos disponibles este mes</b></h5>
        <p className="text-muted small mb-2">
          Solo se contabilizan los documentos electrónicos aceptados por Hacienda.
        </p>
        <Row>
          {documentUsage.map(usage => (
            <Col xs="12" md="6" lg="4" key={usage.documentTypeId} className="mb-3">
              <Alert color={usage.available <= 0 ? "danger" : "info"} className="mb-0">
                <div className="d-flex justify-content-between align-items-center">
                  <span><b>{usage.label}</b></span>
                  <span><b>{usage.available}</b> de {usage.limit}</span>
                </div>
                <div className="small">
                  {usage.available <= 0
                    ? "Alcanzó el máximo de su plan"
                    : `${usage.used} usados este mes`}
                </div>
              </Alert>
            </Col>
          ))}
        </Row>
      </>
    )
  }

  const cancelSuscriptionSection = () => {
    return (tenant.status != "toCancel" && tenant.status != "pendingPaymentMethod") && (<>
      <hr />
      <Row>
        <Col xs="12">
          <h5><b>Cancelar Suscripcion</b></h5>
        </Col>
      </Row>
      <Row>
        <Col xs="12">
          {toCancelSuscription && (
            <Alert color="warning">Su suscripcion a sido cancelada, usted podra tener acceso al sistema de facturacion
              hata el final del actual ciclo de cobro indicado en la campo <b>“Fecha Proximo Pago”</b> </Alert>
          )}
          {!toCancelSuscription && (<Alert color="warning">
            <p>Al cancelar la suscripción:</p>
            <ul>
              <li>Mantendrás acceso al sistema hasta la fecha indicada en el campo <b>“Fecha Proximo Pago”</b>.</li>
              <li>Después de esa fecha, la suscripción se desactivará automáticamente.</li>
              <li>La suscripción de pagos en ONVO Pay será cancelada y no se realizarán más cargos a la tarjeta registrada.</li>
            </ul>
          </Alert>
          )}
        </Col>
      </Row>
      {!toCancelSuscription && (
        <Row>
          <Col xs="12">
            <ConfirmableButton
              title="Cancelar Suscripcion"
              confirmBtnText="Cancelar Suscripcion"
              triggerBtnColor="danger"
              confirmMessage="Esta seguro que desea cancelar su Suscription a Easypos?"
              onConfirm={stopSuscription}>
            </ConfirmableButton>
          </Col>
        </Row>
      )}
    </>
    )
  }

  const MAX_VISIBLE_FEATURES = 6

  // Bloque destacado con los topes mensuales de documentos del plan.
  const renderPlanLimits = (plan: SuscriptionTypeDataType): ReactNode => {
    const labels = getPlanLimitLabels(plan)
    if (labels.length === 0) return null
    return (
      <div className={styles.planLimitsBox}>
        <span className={styles.planLimitsTitle}>Documentos por mes</span>
        <ul className={styles.planLimits}>
          {labels.map(label => (
            <li key={label}>
              <FontAwesomeIcon icon={faFileInvoice} className={styles.planLimitIcon} />
              {label}
            </li>
          ))}
        </ul>
      </div>
    )
  }

  const renderPlanFeaturesList = (features: string[]): ReactNode => {
    const visible = features.slice(0, MAX_VISIBLE_FEATURES)
    const extra = features.length - visible.length
    if (visible.length === 0) return null
    return (
      <ul className={styles.planFeatures}>
        {visible.map(label => (
          <li key={label}>
            <FontAwesomeIcon icon={faCheck} className={styles.planFeatureIcon} />
            {label}
          </li>
        ))}
        {extra > 0 && (
          <li className={styles.planFeaturesMore}>{`+${extra} funciones más`}</li>
        )}
      </ul>
    )
  }

  const renderAvailablePlans = (): ReactNode => {
    if (sortedPlans.length === 0) return null
    return (
      <>
        <hr />
        <div className={styles.plansHeader}>
          <div>
            <h3 className="mb-1">Planes disponibles</h3>
            <p className="mb-0">¿No estás seguro de qué plan elegir?</p>
          </div>
          <Button color="primary" onClick={() => setCompareOpen(true)}>
            Comparar Planes
          </Button>
        </div>
        <Row className="mt-3">
          {sortedPlans.map((plan, index) => {
            const isRecommended = plan.id === recommendedPlanId
            const isCurrent = plan.id === suscriptionType.id
            const isSelected = selectedPlan?.id === plan.id
            const prevPlan = index > 0 ? sortedPlans[index - 1] : undefined
            const deltaFeatures = getDeltaFeatureLabels(plan, prevPlan)
            const cardIsFree = !plan.onvoPriceId
            return (
              <Col xs="12" md="6" lg="4" key={plan.id} className="mb-3">
                <div className={cx([styles.planCard, {
                  [styles.planCardRecommended]: isRecommended,
                  [styles.planCardCurrent]: isCurrent,
                }])}>
                  {isRecommended && (<span className={styles.recommendedBadge}>Recomendado</span>)}
                  {isCurrent && (<span className={styles.currentBadge}>Plan Actual</span>)}
                  <h5 className={styles.planName}>{plan.nombre}</h5>
                  <div className={styles.planPrice}>
                    {cardIsFree
                      ? "Gratis"
                      : formatMoneyValue(Number(plan.monto || 0), getCurrencyNumericId(plan.currencyId))}
                    {!cardIsFree && (<span className={styles.planPricePeriod}> / mes</span>)}
                  </div>
                  <div className={styles.planUsersBox}>
                    <FontAwesomeIcon icon={faUsers} className={styles.planUsersIcon} />
                    <span>{getPlanUsersLabel(plan)}</span>
                  </div>
                  {renderPlanLimits(plan)}
                  {prevPlan && (
                    <p className={styles.planIncludesPrev}>
                      Todo lo del plan <b>{prevPlan.nombre}</b>, más:
                    </p>
                  )}
                  {renderPlanFeaturesList(prevPlan ? deltaFeatures : getPlanFeatureLabels(plan))}
                  {isCurrent ? (
                    <Button color="secondary" outline disabled>Plan Actual</Button>
                  ) : (
                    <Button
                      color={isRecommended ? "success" : "primary"}
                      disabled={loading || isSelected}
                      onClick={() => subscribeToPlan(plan)}
                    >
                      {isSelected ? "Plan seleccionado" : "Suscribirse"}
                    </Button>
                  )}
                </div>
              </Col>
            )
          })}
        </Row>
        {renderUpgradePayment()}
      </>
    )
  }

  // Formulario de pago ONVO que aparece debajo de las tarjetas al seleccionar un plan.
  const renderUpgradePayment = (): ReactNode => {
    if (!selectedPlan) return null
    return (
      <div className={styles.upgradePayment} ref={paymentSectionRef}>
        <hr />
        <Alert color="info" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span>Estás por suscribirte al plan <b>{selectedPlan.nombre}</b>. Completa el pago para activarlo.</span>
          <Button color="link" className="p-0" onClick={cancelSubscription}>Cancelar</Button>
        </Alert>
        <Alert>
          <p className="mb-0">
            ✅ La información de tu tarjeta no es almacenada por Easypos: es procesada de forma segura por nuestro
            socio certificado ONVO Pay.
          </p>
        </Alert>
        <Row>
          <Col xs="12" md={{ offset: 2, size: 8 }} lg={{ offset: 3, size: 6 }}>
            <div ref={onvoUpgradeFields}></div>
          </Col>
        </Row>
      </div>
    )
  }

  // Offcanvas grande de comparación: encabezado (nombres) fijo arriba, botones "Suscribirse"
  // fijos abajo, y solo la lista de funciones hace scroll.
  const renderCompareOffcanvas = (): ReactNode => {
    const plans = sortedPlans
    if (plans.length === 0) return null
    const gridStyle = { gridTemplateColumns: `minmax(180px, 1.6fr) repeat(${plans.length}, minmax(130px, 1fr))` }
    return (
      <DefaultOffCanvas
        className={styles.compareOffcanvas}
        direction="end"
        isOpen={compareOpen}
        toggle={() => setCompareOpen(false)}
        onClosed={() => setCompareOpen(false)}
      >
        <OffcanvasHeader className={styles.compareOffcanvasHeader} toggle={() => setCompareOpen(false)}>
          Comparar Planes
        </OffcanvasHeader>
        <OffcanvasBody className={styles.compareBody}>
          <div className={styles.compareGrid} style={gridStyle}>
            {/* Encabezado: esquina + nombres de planes (fijo arriba) */}
            <div className={cx(styles.compareCell, styles.compareHeadCell, styles.compareCorner, styles.compareCornerTop)}>
              Funciones
            </div>
            {plans.map(plan => {
              const isCurrent = plan.id === suscriptionType.id
              return (
                <div key={plan.id} className={cx(styles.compareCell, styles.compareHeadCell)}>
                  <div className={styles.comparePlanName}>{plan.nombre}</div>
                  <div className={styles.comparePlanPrice}>
                    {!plan.onvoPriceId
                      ? "Gratis"
                      : formatMoneyValue(Number(plan.monto || 0), getCurrencyNumericId(plan.currencyId))}
                  </div>
                  <div className={styles.comparePlanUsers}>{getPlanUsersLabel(plan)}</div>
                  {isCurrent && (<span className={styles.compareCurrentTag}>Plan Actual</span>)}
                </div>
              )
            })}

            {/* Filas de topes mensuales de documentos (destacadas arriba de las funciones).
                Se omite la fila si ningún plan define ese límite. */}
            {DOCUMENT_LIMIT_CATALOG
              .filter(node => plans.some(plan => getDocumentLimit(parseDocumentLimits(plan.documentLimits), node.id) > 0))
              .map(node => (
                <Fragment key={`limit-${node.id}`}>
                  <div className={cx(styles.compareCell, styles.compareFeatureLabel)}>{node.label} / mes</div>
                  {plans.map(plan => {
                    const limit = getDocumentLimit(parseDocumentLimits(plan.documentLimits), node.id)
                    return (
                      <div key={plan.id + node.id} className={cx(styles.compareCell, styles.compareCheckCell)}>
                        {limit > 0 ? <b>{limit}</b> : "Ilimitado"}
                      </div>
                    )
                  })}
                </Fragment>
              ))}

            {/* Filas de funciones (scroll). Se omite la fila si ningún plan la muestra
                (funciones ocultas o no incluidas en ningún plan). */}
            {PLAN_FEATURE_CATALOG
              .filter(node => plans.some(plan => isFeatureVisible(plan, node.id)))
              .map(node => (
                <Fragment key={node.id}>
                  <div className={cx(styles.compareCell, styles.compareFeatureLabel)}>{node.label}</div>
                  {plans.map(plan => (
                    <div key={plan.id + node.id} className={cx(styles.compareCell, styles.compareCheckCell)}>
                      {isFeatureVisible(plan, node.id) && <FontAwesomeIcon icon={faCheck} className={styles.planFeatureIcon} />}
                    </div>
                  ))}
                </Fragment>
              ))}

            {/* Pie: esquina + botones Suscribirse (fijo abajo) */}
            <div className={cx(styles.compareCell, styles.compareFootCell, styles.compareCorner, styles.compareCornerBottom)}></div>
            {plans.map(plan => {
              const isCurrent = plan.id === suscriptionType.id
              return (
                <div key={plan.id} className={cx(styles.compareCell, styles.compareFootCell)}>
                  {isCurrent ? (
                    <Button color="secondary" outline size="sm" disabled block>Plan Actual</Button>
                  ) : (
                    <Button color="success" size="sm" block disabled={loading} onClick={() => subscribeToPlan(plan)}>
                      Suscribirse
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        </OffcanvasBody>
      </DefaultOffCanvas>
    )
  }

  return (
    <PageContainer
      containerClassName={styles.mainContainer}
      title="Administracion de Suscripcion"
      backUrl="/user/profile"
    >
      <div ref={containerRef}>
        <Row>
          <Col xs="5" md="3" lg="3" xl="2">
            <h5><b>Tipo de Suscripcion</b></h5>
          </Col>
          <Col xs md="8">
            {suscriptionType.nombre}
          </Col>
        </Row>
        <Row>
          <Col xs="5" md="3" lg="3" xl="2">
            <h5><b>Monto Mensual</b></h5>
          </Col>
          <Col xs md="8">
            {isFreePlan
              ? "Gratis"
              : formatMoneyValue(Number(suscriptionType.monto || 0), currency?.currency_id || 1)}
          </Col>
        </Row>
        {!isFreePlan && (
          <Row>
            <Col xs="5" md="3" lg="3" xl="2">
              <h5><b>Fecha Proximo Pago</b></h5>
            </Col>
            <Col xs md="8">
              {
                getValidAccessUntilDate()
              }
            </Col>
          </Row>
        )}
        <Row>
          <Col xs="5" md="3" lg="3" xl="2">
            <h5><b>Estado de Suscripcion</b></h5>
          </Col>
          <Col xs md="8">
            <b className={cx([styles.statusWrapper, styles[`status-${tenant.status}`]])}>
              <FontAwesomeIcon {...getSuscriptionStatusIconProps()} />
              {getSuscriptionStatus()}</b>
          </Col>
        </Row>
        {renderDocumentUsage()}
        {isFreePlan && renderAvailablePlans()}
        {showPaymentForm && <hr />}
        <div className={styles.paymentForm} style={{ height: showPaymentForm ? "auto" : "0" }}>
          <Alert>
            <p>
              Para continuar disfrutando del servicio de facturación electrónica de Easypos, es necesario configurar un método de pago válido para su suscripción.
            </p>
            <p>
              ✅ Seguridad garantizada:
              La información de su tarjeta de crédito no es almacenada por Easypos. Todos los datos relacionados con pagos son procesados y resguardados de forma segura por nuestro socio certificado ONVO Pay.
            </p>
            <p>
              🔒 El sitio web de Easypos cuenta con un certificado SSL activo, lo que asegura que toda la información se transmite de forma cifrada y segura directamente a ONVO Pay, cumpliendo con los más altos estándares de protección.
            </p>
          </Alert>
          {tenant?.status == "pendingPaymentMethod" && (
            <Alert color="warning">
              <p>
                ⚠️ Importante:
                Si no se asigna un método de pago antes de la fecha indicada en el campo “Próximo pago”, su suscripción será desactivada automáticamente en esa fecha.
              </p><p>
                Para evitar interrupciones en el servicio, le recomendamos completar este proceso lo antes posible.
              </p>
            </Alert>
          )}
          <Row>
            <Col
              xs={{
                offset: 2,
                size: 8
              }}
              md={{
                offset: 2,
                size: 8
              }}
              lg={{
                offset: 2,
                size: 6
              }}>
              <div ref={onvoFields}></div>
            </Col>
          </Row>
        </div>
        {!isFreePlan && cancelSuscriptionSection()}
        {!isFreePlan && historialDePagos()}
        {renderCompareOffcanvas()}
        <LoadingScreen isLoading={loading} />
      </div>
    </PageContainer>
  );
}

export default TenantManage;
